import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import '@/Sections.css';

function ServicePage({ meta, h1, intro, bullets, videos, price, others }) {
  useEffect(() => {
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
    window.scrollTo(0, 0);
  }, [meta]);

  return (
    <div className="service-page" data-testid="service-page">
      <div className="container">
        <Link to="/" className="sp-back" data-testid="service-page-back"><ArrowLeft size={16} /> Zur Startseite</Link>
        <h1 className="sp-title">{h1}</h1>
        <p className="sp-intro">{intro}</p>

        <div className="sp-bullets">
          {bullets.map((b, i) => (
            <div className="sp-bullet" key={i}><CheckCircle size={18} /> <span>{b}</span></div>
          ))}
        </div>

        <h2 className="sp-subheading">Beispiele aus unserer Produktion</h2>
        <div className="sp-videos">
          {videos.map((v) => (
            <div className="sp-video" key={v.id}>
              <div className="sp-video-frame">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                  title={v.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p>{v.title}</p>
            </div>
          ))}
        </div>

        <div className="sp-price-box">
          <p className="sp-price">{price}</p>
          <Link to="/#contact" className="sp-cta" data-magnetic="true" data-testid="service-page-cta">
            Jetzt Projekt anfragen <ArrowRight size={18} />
          </Link>
        </div>

        <div className="sp-others">
          <span>Weitere Leistungen:</span>
          {others.map((o) => (
            <Link key={o.to} to={o.to}>{o.label}</Link>
          ))}
        </div>

        <div className="sp-footer">
          <span>© 2026 mAIsblau Videoproduktion Wien</span>
          <Link to="/impressum">Impressum</Link>
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </div>
    </div>
  );
}

export function KiWerbespot() {
  return (
    <ServicePage
      meta={{
        title: 'KI-Werbespot Wien – AI Werbevideos erstellen lassen | mAIsblau',
        description: 'KI-Werbespot aus Wien: cineastische AI-Werbevideos für Web, Social & regionales TV. Gesteuert von Filmemacher Andreas Hofmarcher, ab €900 netto. Jetzt anfragen!',
      }}
      h1="KI-Werbespot aus Wien"
      intro="Werbespots, die es ohne KI so nie gegeben hätte: mAIsblau produziert KI-generierte Werbevideos für Unternehmen in Wien und Umgebung – gesteuert von Filmemacher Andreas Hofmarcher mit TV-Erfahrung, nicht von Tool-Bedienern. Mehrere KI-Spots von mAIsblau laufen bereits im regionalen Fernsehen."
      bullets={[
        'Kino-Look ohne Dreh, Crew und Studiomiete',
        'Erste Ergebnisse in Tagen statt Wochen',
        'Varianten für A/B-Tests, Formate für alle Kanäle',
        'Schnitt, Color Grading & Sounddesign inklusive',
        'TV-erprobt: KI-Spots von mAIsblau laufen im regionalen Fernsehen',
      ]}
      videos={[
        { id: 'jJIPN66I4KM', title: 'Indian Living – KI-Werbespot (im TV ausgestrahlt)' },
        { id: 'orvlnanAlTc', title: 'WINSTAGE Wiener Neustadt – KI-Werbespot' },
        { id: 'io5WLDhJRPI', title: 'Winpark West – KI-Werbespot' },
      ]}
      price="KI-Werbespots ab €900 netto – Kampagnen mit mehreren Szenen ab €2.200 netto."
      others={[
        { to: '/ki-produktvideo-wien', label: 'KI-Produktvideo' },
        { to: '/ki-musikvideo-wien', label: 'KI-Musikvideo' },
      ]}
    />
  );
}

export function KiProduktvideo() {
  return (
    <ServicePage
      meta={{
        title: 'KI-Produktvideo Wien – AI Produktreels erstellen lassen | mAIsblau',
        description: 'Fotorealistische KI-Produktvideos aus Wien: dein Produkt im perfekten Licht – ohne Set, ohne Musterversand. Ab €900 netto. Jetzt anfragen!',
      }}
      h1="KI-Produktvideo aus Wien"
      intro="Dein Produkt verdient großes Kino – auch ohne Drehtag. mAIsblau erstellt fotorealistische KI-Produktreels aus echten Produktfotos oder kurzen Aufnahmen und hält Form, Farbe und Logo konsistent über alle Szenen. Ideal für Launches, Shops und Social Ads."
      bullets={[
        'Fotorealistisch & markenkonsistent (Form, Farbe, Logo)',
        'Funktioniert mit Produktfotos – kein Musterversand nötig',
        'Auch für Produkte, die es erst als Prototyp gibt',
        'Hybrid möglich: echtes Produkt real gedreht, Welt generiert',
        'Formate für Shop, Social & Ads inklusive',
      ]}
      videos={[
        { id: 'Mj7qp557RwI', title: 'Indian Living – KI-Produktvideo' },
        { id: 'FtEF5EWEIb4', title: 'La Pelote Künstlerbedarf – KI-Promo' },
        { id: 'h3kIhVnckhQ', title: 'Meister Ambrosius – KI-Serie für La Pelote' },
      ]}
      price="KI-Produktreels ab €900 netto – mehrteilige Kampagnen ab €2.200 netto."
      others={[
        { to: '/ki-werbespot-wien', label: 'KI-Werbespot' },
        { to: '/ki-musikvideo-wien', label: 'KI-Musikvideo' },
      ]}
    />
  );
}

export function KiMusikvideo() {
  return (
    <ServicePage
      meta={{
        title: 'KI-Musikvideo Wien – AI Musikvideos produzieren lassen | mAIsblau',
        description: 'KI-Musikvideos aus Wien: durchkomponierte Videowelten für deinen Song – leistbar auch ohne Label-Budget. Vom Filmemacher geschnitten. Jetzt anfragen!',
      }}
      h1="KI-Musikvideo aus Wien"
      intro="Ein Musikvideo, das deine Musik visuell erzählt – ohne Location-Scouting und Drehwochenende. Andreas generiert Welten, die zu deinem Sound passen, und gibt ihnen mit handwerklichem Schnitt, Pacing und Color Grading eine echte Film-Handschrift."
      bullets={[
        'Durchkomponierte Welten statt zufälliger Clips',
        'Schnitt im Rhythmus deiner Musik',
        'Leistbar auch ohne Label-Budget',
        'Hybrid möglich: echte Performance + generierte Welten',
        'Fertige Formate für YouTube, Reels & TikTok',
      ]}
      videos={[
        { id: 'G7LjvT17pyc', title: 'Notfallsong (Beate Glatz) – Offizielles KI-Musikvideo' },
        { id: 'KPT7jQZSi9c', title: 'Atra Vellion – Songcontest (WINSTAGE)' },
      ]}
      price="Musikvideos kalkulieren wir individuell – erzähl uns von Song und Idee."
      others={[
        { to: '/ki-werbespot-wien', label: 'KI-Werbespot' },
        { to: '/ki-produktvideo-wien', label: 'KI-Produktvideo' },
      ]}
    />
  );
}
